import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '../../../utils/tailwind';

type PopoverType = {
    children: React.ReactNode;
    content: React.ReactNode | string;
    position?: 'bottom' | 'top' | 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
    arrowPosition?: number;
    block?: boolean;
};

export const Popover: React.FC<PopoverType> = ({
    children,
    content,
    position = 'top-left',
    arrowPosition,
    block = false,
}) => {
    const [hoveredOnTrigger, setHoveredOnTrigger] = useState(false);
    const [hoveredOnContent, setHoveredOnContent] = useState(false);
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
    const [tooltipContent, setTooltipContent] = useState(content);

    const triggerRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (hoveredOnTrigger || hoveredOnContent) {
            setTooltipVisible(true);
        } else {
            setTooltipVisible(false);
        }
    }, [hoveredOnTrigger, hoveredOnContent]);

    const calculateTooltipPosition = () => {
        if (tooltipRef.current && (triggerRef.current || iconRef.current)) {
            if (triggerRef.current) {
                const triggerRect = triggerRef.current.getBoundingClientRect();
                const tooltipRect = tooltipRef.current.getBoundingClientRect();

                let top = 0;
                let left = 0;

                switch (position) {
                    case 'bottom':
                        top = triggerRect.bottom + window.scrollY;
                        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2 + window.scrollX;
                        break;
                    case 'top':
                        top = triggerRect.top - tooltipRect.height + window.scrollY;
                        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2 + window.scrollX;
                        break;
                    case 'top-left':
                        top = triggerRect.top - tooltipRect.height + window.scrollY;
                        left = triggerRect.left + window.scrollX;
                        break;
                    case 'top-right':
                        top = triggerRect.top - tooltipRect.height + window.scrollY;
                        left = triggerRect.right - tooltipRect.width + window.scrollX;
                        break;
                    case 'bottom-left':
                        top = triggerRect.bottom + window.scrollY;
                        left = triggerRect.left + window.scrollX;
                        break;
                    case 'bottom-right':
                        top = triggerRect.bottom + window.scrollY;
                        left = triggerRect.right - tooltipRect.width + window.scrollX;
                        break;
                    default:
                        top = triggerRect.bottom + window.scrollY;
                        left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2 + window.scrollX;
                        break;
                }

                setTooltipPosition({ top, left });
            }
        }
    };

    useEffect(() => {
        if (tooltipVisible) {
            calculateTooltipPosition();
        }
    }, [tooltipVisible, tooltipContent]);

    useEffect(() => {
        setTooltipContent(content);
    }, [content]);

    const handleTriggerMouseEnter = () => {
        setHoveredOnTrigger(true);
    };

    const handleTriggerMouseLeave = () => {
        setHoveredOnTrigger(false);
    };

    const handleContentMouseEnter = () => {
        setHoveredOnContent(true);
    };

    const handleContentMouseLeave = () => {
        setHoveredOnContent(false);
    };

    const isLeftArrow = position === 'top-left' || position === 'bottom-left';
    const isRightArrow = position === 'top-right' || position === 'bottom-right';

    let arrowStyle: React.CSSProperties | undefined;

    if (isLeftArrow) {
        arrowStyle = { left: 'var(--arrow-position)' };
    }

    if (isRightArrow) {
        arrowStyle = { right: 'var(--arrow-position)' };
    }

    return (
        <span className={cn('relative', block ? 'block' : 'inline-flex')}>
            <span
                ref={triggerRef}
                className={cn(block ? 'block' : 'inline-flex', 'items-center gap-xsmall')}
                onMouseEnter={handleTriggerMouseEnter}
                onMouseLeave={handleTriggerMouseLeave}
            >
                <span className={cn(block ? 'block' : 'inline-block')}>{children}</span>
            </span>
            {tooltipVisible &&
                createPortal(
                    <span
                        ref={tooltipRef}
                        className={cn(
                            'absolute z-[9999] flex max-w-[30rem] px-small py-small normal-case',
                            position === 'top-left' && '-translate-x-[3.2rem]',
                            position === 'top-right' && 'translate-x-[3.2rem]',
                            position === 'bottom-right' && 'translate-x-[3.2rem]',
                            position === 'bottom-left' && '-translate-x-[3.2rem]',
                        )}
                        style={{ top: tooltipPosition?.top, left: tooltipPosition?.left }}
                        onMouseEnter={handleContentMouseEnter}
                        onMouseLeave={handleContentMouseLeave}
                    >
                        <span
                            className="relative w-full rounded-medium bg-surface p-small text-small leading-medium shadow-strong"
                            style={
                                {
                                    '--arrow-position': `${arrowPosition ?? 1.6}rem`,
                                } as React.CSSProperties
                            }
                        >
                            <span
                                className={cn(
                                    'pointer-events-none absolute h-0 w-0 border-[0.8rem] border-solid border-transparent',
                                    position === 'bottom' && 'bottom-full left-1/2 -translate-x-1/2 border-b-surface',
                                    position === 'top' && 'left-1/2 top-full -translate-x-1/2 border-t-surface',
                                    position === 'top-left' && 'top-full border-t-surface',
                                    position === 'top-right' && 'top-full border-t-surface',
                                    position === 'bottom-right' && 'bottom-full border-b-surface',
                                    position === 'bottom-left' && 'bottom-full border-b-surface',
                                )}
                                style={arrowStyle}
                            />
                            {content}
                        </span>
                    </span>,
                    document.body,
                )}
        </span>
    );
};

export default Popover;
